import { createSelector, ParametricSelector } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import { UpgradeModalVersion } from 'common/billing/constants'
import { SubscriptionAction } from 'common/billing/constants/SubscriptionAction'
import { getIsBrazilInAppCardAttachmentEnabled } from 'common/billing/entities/billingInfo/selectors'
import {
  getCartProducts,
  getIsAiOnlyCart,
  isAiOnlyCart,
} from 'common/billing/entities/cart/selectors'
import { getRootBillingState } from 'common/billing/entities/miscellaneous/selectors'
import { getIsUpgradeFromTrialToPaidEnabled } from 'common/billing/entities/pro/selectors'
import {
  getSubscriptionAction,
  getSubscriptionInfo,
} from 'common/billing/entities/subscription/selectors'
import { UpgradeSource, UpgradeSourceType } from 'common/billing/interfaces'
import { UpgradeRequest } from 'common/billing/interfaces/upgradeRequest'
import { formatUpgradeSource } from 'common/builder/utils/upgradeEventUtils'
import {
  getBillingAddressFlags,
  getInAppCardAttachmentFlags,
  isTrialAvailable,
} from 'common/core/selectors/appSelectors'

const defaultCaption = l.translate('Upgrade to Pro')
const captionMap: Record<SubscriptionAction, string> = {
  [SubscriptionAction.CONTINUE_CHECKOUT]: l.translate('Verify Payment'),
  [SubscriptionAction.RENEW]: l.translate('Renew Subscription'),
  [SubscriptionAction.ACTIVATE_TRIAL]: l.translate('Activate Pro Trial'),
  [SubscriptionAction.UPGRADE]: defaultCaption,
  [SubscriptionAction.CHANGE_PLAN]: defaultCaption,
  [SubscriptionAction.NONE]: '',
}

export const getUpgradeRequest = createSelector(
  getRootBillingState,
  getIsUpgradeFromTrialToPaidEnabled,
  (billing, isUpgradeFromTrialToProEnabled): UpgradeRequest => {
    const requestPaid = Boolean(
      billing.upgradeRequest.requestPaid && isUpgradeFromTrialToProEnabled,
    )

    return {
      ...billing.upgradeRequest,
      requestPaid,
    }
  },
)

export const getUpgradeCaption: ParametricSelector<RootState, string | void, string> =
  createSelector([getSubscriptionAction], (subscriptionAction): string => {
    return captionMap[subscriptionAction]
  })

export const getUpgradeModalVersionConditions = createSelector(
  [isTrialAvailable, getIsAiOnlyCart, (state) => getSubscriptionInfo(state).isTrial],
  (trialAvailable, isAiOnlyCart, isTrial): Record<UpgradeModalVersion, boolean> => ({
    [UpgradeModalVersion.SIMPLE_TRIAL]: trialAvailable,
    [UpgradeModalVersion.UPGRADE_AI]: isAiOnlyCart && !isTrial,
    [UpgradeModalVersion.UPGRADE_AI_FROM_TRIAL]: isAiOnlyCart && isTrial,
    [UpgradeModalVersion.UPGRADE]: true,
    [UpgradeModalVersion.PAYWALL]: true,
  }),
)

export const getUpgradeModalVersion = createSelector(
  [getCartProducts, getUpgradeRequest, getUpgradeModalVersionConditions, getSubscriptionInfo],
  (cart, request, variantConditions, subscriptionInfo): UpgradeModalVersion => {
    if (isAiOnlyCart(cart)) {
      if (subscriptionInfo.isTrial) {
        return UpgradeModalVersion.UPGRADE_AI_FROM_TRIAL
      }
      return UpgradeModalVersion.UPGRADE_AI
    }

    if (request.variant && variantConditions[request.variant]) {
      return request.variant
    }

    return UpgradeModalVersion.PAYWALL
  },
)

export const getUpgradeRequestSource = createSelector(
  [getUpgradeRequest],
  (request) => request.source,
)

export const getModalAnalyticsData = createSelector(
  [getUpgradeRequest, getCartProducts, getSubscriptionAction, getUpgradeModalVersion],
  (upgradeRequest, products, subscriptionAction, modalVersion) => {
    return {
      source: formatUpgradeSource(upgradeRequest.source),
      products: products.plans.map((plan) => plan.slug),
      addOns: products.addOns.map((addOn) => addOn.slug),
      proNodes: upgradeRequest.proNodes || [],
      uuid: upgradeRequest.uuid,
      modalVersion,
      subscriptionAction,
    }
  },
)

export const getShouldUseCheckoutFlow = createSelector(
  getInAppCardAttachmentFlags,
  getBillingAddressFlags,
  getUpgradeRequest,
  getIsBrazilInAppCardAttachmentEnabled,
  (
    inAppCardAttachmentExperiment,
    billingAddressExperiment,
    request,
    isBrazilInAppCheckoutEnabled,
  ) => {
    if (isBrazilInAppCheckoutEnabled) {
      return true
    }

    const VALID_EXPERIMENT_UPGRADE_SOURCES: UpgradeSourceType[] = [
      UpgradeSource.NOTIFICATION_BAR_PRO_TRIAL_EXPERIMENT,
      UpgradeSource.SIDEBAR,
      UpgradeSource.EXPIRED_NOTIFY_BAR,
    ]

    return (
      (inAppCardAttachmentExperiment.isEnabled || billingAddressExperiment.isEnabled) &&
      VALID_EXPERIMENT_UPGRADE_SOURCES.includes(request.source)
    )
  },
)
